import { FacilityPerformanceResponse } from "@features/ax/assetRegistry/facilities/types";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import React from "react";
import { useNavigate } from "react-router";

interface OperationalInsightsProps {
  performanceData?: FacilityPerformanceResponse;
}

const NoDataMessage = () => (
  <div className="flex items-center justify-center py-8 text-gray-500">
    <p>No data available</p>
  </div>
);

export const OperationalInsights: React.FC<OperationalInsightsProps> = ({
  performanceData,
}) => {
  const navigate = useNavigate();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              Assets Approaching Replacement
            </CardTitle>
            <CustomTooltip
              message={
                " Assets nearing end of useful life that require replacement planning and budget allocation."
              }
            />
          </div>
        </CardHeader>
        <CardContent>
          {performanceData?.result?.assetApproachingReplacement?.length > 0 ? (
            <div className="space-y-4">
              {performanceData.result.assetApproachingReplacement.map(
                (asset) => (
                  <div
                    key={asset.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-white shadow-sm border border-gray-200"
                  >
                    <div>
                      <p className="font-medium text-gray-800">{asset.name}</p>
                      <p className="text-sm text-gray-600">
                        {asset.id} • Est. Cost: {asset.estimatedCost}
                      </p>
                      <p className="text-xs text-gray-500">
                        Expected: {asset.expectedDate}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge
                        variant={
                          asset.priority === "high"
                            ? "destructive"
                            : asset.priority === "medium"
                            ? "secondary"
                            : "default"
                        }
                      >
                        {asset.priority}
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          navigate(`/ax/asset-registry/assets/view/${asset.id}`)
                        }
                      >
                        View
                      </Button>
                    </div>
                  </div>
                )
              )}
            </div>
          ) : (
            <NoDataMessage />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              Frequent Failure Assets
            </CardTitle>
            <CustomTooltip
              message={
                "Assets with high failure rates that may require enhanced maintenance strategies or replacement consideration."
              }
            />
          </div>
        </CardHeader>
        <CardContent>
          {performanceData?.result?.frequentFailureAsset?.length > 0 ? (
            <div className="space-y-4">
              {performanceData.result.frequentFailureAsset.map((asset) => (
                <div
                  key={asset.requestAccountNo}
                  className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm border border-gray-200"
                >
                  <div>
                    <p className="font-medium text-gray-800">
                      {asset.requestUsername ?? "NA"}
                    </p>
                    <p className="text-sm text-gray-600">
                      {asset.requestAccountNo} • {asset.jobCardCount} failures
                    </p>
                    <p className="text-xs text-gray-500">
                      Last: {asset.latestCreatedDate}
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        navigate(
                          `/ax/asset-registry/assets/view/${asset.requestId}`
                        )
                      }
                    >
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <NoDataMessage />
          )}
        </CardContent>
      </Card>
    </div>
  );
};
